import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RAG_META } from "@/lib/rag";
import { ClipboardEdit, TrendingUp, ShieldCheck, Moon, Download, ChevronDown, ChevronRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import MemberOverview from "@/components/MemberOverview";
import DateField from "@/components/DateField";
import { fmtDate, fmtDateShort, exportFilename } from "@/lib/timefmt";
import { toast } from "sonner";

const EXPORT_MODES = [
  { id: "not_exported", label: "Not exported", desc: "Entries that have never been exported" },
  { id: "day", label: "By day", desc: "One specific day" },
  { id: "period", label: "Selectable period", desc: "A date range" },
  { id: "all", label: "All data", desc: "Every entry you have submitted" },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [roleName, setRoleName] = useState("");
  const [showExport, setShowExport] = useState(false);
  useEffect(() => { api.get("/entries").then((r) => setEntries(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!user?.company_id) return;
    api.get("/companies").then((r) => setCompanyName(r.data.find((c) => c.id === user.company_id)?.name || "")).catch(() => {});
    api.get(`/roles?company_id=${user.company_id}`).then((r) => setRoleName(r.data.find((x) => x.id === user.role_id)?.name || "")).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const last = entries[0];
  const meta = last ? RAG_META[last.rag] : RAG_META.green;
  const chart = [...entries].slice(0, 14).reverse().map((e) => ({ date: fmtDateShort(e.date), score: Number(e.score_total) || 0 }));
  const totalRest = last ? (last.rest_periods || []).reduce((s, r) => s + (r.total_hours || 0), 0) : 0;
  const isAdmin = (user?.role === "admin" || !!user?.admin_level) && user?.admin_mode !== false;
  const canViewBoard = isAdmin || ["command", "supervisor"].includes(user?.privilege);

  if (canViewBoard) return <MemberOverview />;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num" data-testid="dashboard-title">Fatigue Board · {new Date().toLocaleDateString()}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">
            Good watch, {user?.first_name}
          </h1>
          <p className="text-slate-400 mt-1 text-sm">Live crew readiness snapshot and rolling risk trend.</p>
        </div>
        <Link to="/fatigue" data-testid="new-entry-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors">
          <ClipboardEdit className="h-4 w-4" /> Log Fatigue Entry
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-4 p-5 rounded-lg border border-slate-800 bg-slate-900/60">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Latest Risk Score</div>
          {last ? (
            <>
              <div className="flex items-baseline gap-3 mt-2">
                <div className="text-5xl font-mono-num" data-testid="dash-latest-score">{Number(last.score_total).toFixed(1)}</div>
                <div className={`px-2.5 py-1 rounded text-[11px] font-semibold uppercase tracking-widest ${meta.cls}`}>
                  <span className={`inline-block h-2 w-2 rounded-full mr-2 align-middle ${meta.dot}`} />
                  {meta.label}
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-500 font-mono-num">Entry {fmtDate(last.date)} · {last.timezone}</div>
            </>
          ) : (
            <div className="mt-2 text-slate-400 text-sm">No entries yet. Log your first daily fatigue check.</div>
          )}
        </div>

        <Stat icon={TrendingUp} label="Entries logged" value={entries.length} testId="stat-entries" />
        <Stat icon={Moon} label="Rest hours (last entry)" value={`${totalRest.toFixed(1)}h`} testId="stat-rest" />
        <Stat icon={ShieldCheck} label="Company · Role" value={companyName ? `${companyName}${roleName ? ` · ${roleName}` : ""}` : "—"} testId="stat-assignment" />

        <div className="lg:col-span-12 p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="trend-chart">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Risk Trend</div>
              <div className="font-heading text-xl font-semibold">Last 14 entries</div>
            </div>
          </div>
          <div className="h-64 -mx-2">
            {chart.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chart}>
                  <CartesianGrid stroke="#1F2937" vertical={false} />
                  <XAxis dataKey="date" stroke="#64748B" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#64748B" tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1F2937", borderRadius: 6 }} />
                  <Line type="monotone" dataKey="score" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3, fill: "#3B82F6" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-slate-500 text-sm">No data yet.</div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <button onClick={() => setShowExport((v) => !v)} data-testid="export-panel-toggle"
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
          {showExport ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <span className="font-heading text-base font-semibold text-slate-200">Export My Data</span>
          <span className="text-xs text-slate-500">CSV or colour-coded XLSX</span>
        </button>
        {showExport && <ExportPanel user={user} />}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="lg:col-span-2.5 col-span-1 lg:col-span-2 p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid={testId}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-400" />
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{label}</div>
      </div>
      <div className="text-2xl font-mono-num mt-2">{value}</div>
    </div>
  );
}

function ExportPanel({ user }) {
  const [mode, setMode] = useState("not_exported");
  const [date, setDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [includeExported, setIncludeExported] = useState(false);
  const [color, setColor] = useState(false);
  const [fmt, setFmt] = useState("csv");
  const [busy, setBusy] = useState(false);
  const [taskFrom, setTaskFrom] = useState("");
  const [taskTo, setTaskTo] = useState("");
  const [taskFmt, setTaskFmt] = useState("csv");
  const [taskBusy, setTaskBusy] = useState(false);

  const downloadTasks = async () => {
    if (!taskFrom || !taskTo) { toast.error("Pick a start and end date"); return; }
    setTaskBusy(true);
    try {
      const res = await api.get("/tasks/export", { params: { from_date: taskFrom, to_date: taskTo, format: taskFmt }, responseType: "blob" });
      const count = res.headers["x-export-count"];
      const mime = taskFmt === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "text/csv";
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFilename(`crew-ready-tasks-${taskFrom}-to-${taskTo}`, taskFmt, user);
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${count ?? "?"} task(s)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setTaskBusy(false); }
  };

  const download = async () => {
    if (mode === "day" && !date) { toast.error("Pick a day"); return; }
    if (mode === "period" && (!fromDate || !toDate)) { toast.error("Pick a start and end date"); return; }
    setBusy(true);
    try {
      const params = { mode, include_exported: includeExported, color, fmt };
      if (mode === "day") params.date = date;
      if (mode === "period") { params.from_date = fromDate; params.to_date = toDate; }
      const res = await api.get("/export/my", { params, responseType: "blob" });
      const count = res.headers["x-export-count"];
      const mime = fmt === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "text/csv";
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFilename(`crew-ready-${mode}`, fmt, user);
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${count ?? "?"} entr${count === "1" ? "y" : "ies"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 max-w-xl" data-testid="export-panel">
      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 space-y-3" data-testid="export-form">
        {EXPORT_MODES.map((m) => (
          <label key={m.id} className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
            mode === m.id ? "border-blue-600/50 bg-blue-600/10" : "border-slate-800 hover:bg-slate-900"}`}
            data-testid={`export-mode-${m.id}`}>
            <input type="radio" name="export-mode" checked={mode === m.id} onChange={() => setMode(m.id)} className="accent-blue-600 mt-1" />
            <span>
              <span className="block text-sm font-medium">{m.label}</span>
              <span className="block text-xs text-slate-500">{m.desc}</span>
            </span>
          </label>
        ))}
        {mode === "day" && (
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Day</label>
            <DateField value={date} onChange={setDate} testId="export-day" />
          </div>
        )}
        {mode === "period" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">From</label>
              <DateField value={fromDate} onChange={setFromDate} testId="export-from" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">To</label>
              <DateField value={toDate} onChange={setToDate} testId="export-to" />
            </div>
          </div>
        )}
        <label className="flex items-center gap-2.5 text-sm pt-1">
          <input type="checkbox" checked={includeExported} onChange={(e) => setIncludeExported(e.target.checked)}
            data-testid="export-include-exported" className="accent-blue-600 h-4 w-4" />
          Include entries already exported (ignore the &quot;already exported&quot; flag)
        </label>
        <label className="flex items-center gap-2.5 text-sm">
          <input type="checkbox" checked={color} onChange={(e) => setColor(e.target.checked)}
            data-testid="export-color" className="accent-blue-600 h-4 w-4" />
          Colour-coded answers (RAG colour and value; XLSX cells get coloured backgrounds)
        </label>
        <div className="flex rounded-md border border-slate-800 overflow-hidden">
          {["csv", "xlsx"].map((f) => (
            <button key={f} onClick={() => setFmt(f)} data-testid={`export-fmt-${f}`}
              className={`flex-1 px-3 py-2 text-sm transition-colors ${fmt === f ? "bg-blue-600/20 text-blue-300" : "bg-slate-900 text-slate-400 hover:text-white"}`}>
              {f.toUpperCase()}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 leading-relaxed">
          Exported entries are marked as &quot;exported&quot; once the file is generated, so they are excluded from
          future exports unless you tick the box above. Every export is recorded in the admin export log.
        </p>
        <button onClick={download} disabled={busy} data-testid="export-download-btn"
          className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors disabled:opacity-60">
          <Download className="h-4 w-4" /> {busy ? "Preparing…" : `Download ${fmt.toUpperCase()}`}
        </button>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 space-y-3 mt-5" data-testid="tasks-export-card">
        <div className="font-heading text-lg font-semibold">Tasks export</div>
        <p className="text-xs text-slate-500">Tasks you logged or were added to — includes brief/debrief, modes, sequences, status and remarks.</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">From</label>
            <DateField value={taskFrom} onChange={setTaskFrom} testId="tasks-export-from" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">To</label>
            <DateField value={taskTo} onChange={setTaskTo} testId="tasks-export-to" />
          </div>
        </div>
        <div className="flex rounded-md border border-slate-800 overflow-hidden">
          {["csv", "xlsx"].map((f) => (
            <button key={f} onClick={() => setTaskFmt(f)} data-testid={`tasks-export-fmt-${f}`}
              className={`flex-1 px-3 py-2 text-sm transition-colors ${taskFmt === f ? "bg-blue-600/20 text-blue-300" : "bg-slate-900 text-slate-400 hover:text-white"}`}>
              {f.toUpperCase()}
            </button>
          ))}
        </div>
        <button onClick={downloadTasks} disabled={taskBusy} data-testid="tasks-export-download"
          className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors disabled:opacity-60">
          <Download className="h-4 w-4" /> {taskBusy ? "Preparing…" : `Download ${taskFmt.toUpperCase()}`}
        </button>
      </div>
    </div>
  );
}
